import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import type { CanvasDataset } from '../../canvas-catalogue.util';
import {
  ChartsDateRangePickerComponent,
  type DateRange,
} from '../../charts/date-range-picker/charts-date-range-picker.component';
import type { StacBbox } from '../canvas-map-ol.utils';
import {
  CanvasStacApiService,
  type CanvasStacItem,
} from './canvas-stac-api.service';
import { resolveStacItemBbox } from './canvas-stac-bbox.utils';
import {
  buildStacDatetimeInterval,
  formatStacDateTime,
} from './canvas-stac-datetime.util';

export interface CanvasStacPlotPayload {
  imageUrl: string | null;
  bbox: StacBbox;
  properties: Record<string, unknown>;
}

@Component({
  selector: 'lib-canvas-stac-list-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartsDateRangePickerComponent],
  templateUrl: './canvas-stac-list-modal.component.html',
  styleUrl: './canvas-stac-list-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasStacListModalComponent implements OnChanges {
  @Input() open = false;
  @Input() dataset: CanvasDataset | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() plot = new EventEmitter<CanvasStacPlotPayload>();

  private readonly stacApi = inject(CanvasStacApiService);

  readonly initialLoading = signal(false);
  readonly pageLoading = signal(false);
  readonly items = signal<CanvasStacItem[]>([]);
  readonly error = signal<string | null>(null);
  readonly previewUrl = signal<string | null>(null);
  readonly hasNext = signal(false);
  readonly isFirstPage = signal(true);
  /** Picker selection (applied only after Apply Filter). */
  readonly draftFromRange = signal<DateRange>({ startDate: null, endDate: null });
  readonly draftToRange = signal<DateRange>({ startDate: null, endDate: null });
  /** Applied filter values (yyyy-mm-dd) sent to the STAC API. */
  readonly appliedFromIso = signal('');
  readonly appliedToIso = signal('');
  readonly dateFilterError = signal<string | null>(null);

  readonly endPickerMinDate = computed(() => {
    const from = this.draftFromRange().startDate;
    if (!from) return null;
    const min = new Date(from);
    min.setHours(0, 0, 0, 0);
    return min;
  });

  readonly canApplyDateFilter = computed(() => {
    const from = this.draftFromRange().startDate;
    const to = this.draftToRange().startDate;
    if (!from && !to) return true;
    if (!from || !to) return false;
    return this.toIsoDate(to) >= this.toIsoDate(from);
  });

  private nextUrl: string | null = null;
  private paginationHistory: string[] = [];
  private readonly pageLimit = 12;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['dataset']) && this.open && this.dataset) {
      this.reset();
      void this.loadPage(null);
    }
    if (changes['open'] && !this.open) {
      this.reset();
    }
  }

  private reset(): void {
    this.items.set([]);
    this.error.set(null);
    this.previewUrl.set(null);
    this.draftFromRange.set({ startDate: null, endDate: null });
    this.draftToRange.set({ startDate: null, endDate: null });
    this.appliedFromIso.set('');
    this.appliedToIso.set('');
    this.dateFilterError.set(null);
    this.nextUrl = null;
    this.paginationHistory = [];
    this.hasNext.set(false);
    this.isFirstPage.set(true);
  }

  onDraftFromChange(range: DateRange | null): void {
    const next = range ?? { startDate: null, endDate: null };
    this.draftFromRange.set(next);
    this.dateFilterError.set(null);

    const from = next.startDate;
    const to = this.draftToRange().startDate;
    if (from && to && this.toIsoDate(to) < this.toIsoDate(from)) {
      this.draftToRange.set({ startDate: null, endDate: null });
    }
  }

  onDraftToChange(range: DateRange | null): void {
    this.draftToRange.set(range ?? { startDate: null, endDate: null });
    this.dateFilterError.set(null);
  }

  applyDateFilter(): void {
    if (!this.canApplyDateFilter()) {
      const from = this.draftFromRange().startDate;
      const to = this.draftToRange().startDate;
      if ((from && !to) || (!from && to)) {
        this.dateFilterError.set(
          'Select both start and end dates, or clear both to remove the filter.',
        );
      } else if (from && to) {
        this.dateFilterError.set('End date must be on or after the start date.');
      }
      return;
    }

    const from = this.draftFromRange().startDate;
    const to = this.draftToRange().startDate;
    this.dateFilterError.set(null);
    this.appliedFromIso.set(from ? this.toIsoDate(from) : '');
    this.appliedToIso.set(to ? this.toIsoDate(to) : '');
    this.paginationHistory = [];
    this.isFirstPage.set(true);
    void this.loadPage(null);
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  formatItemDateTime(item: CanvasStacItem): string {
    const raw = item.properties?.['datetime'];
    if (typeof raw !== 'string' || !raw.trim()) {
      return 'N/A';
    }
    return formatStacDateTime(raw);
  }

  trackByItemId(_index: number, item: CanvasStacItem): string {
    return item.id;
  }

  private serverUrl(): string {
    const url =
      this.dataset?.stacServerUrl?.trim() ||
      this.dataset?.resourceServerUrl?.trim() ||
      '';
    if (!url) {
      throw new Error('This dataset has no STAC resource server URL.');
    }
    return url;
  }

  private buildItemsListUrl(collectionId: string): string {
    const params = new URLSearchParams({ limit: String(this.pageLimit) });
    const datetime = buildStacDatetimeInterval(
      this.appliedFromIso(),
      this.appliedToIso(),
    );
    if (datetime) {
      params.set('datetime', datetime);
    }
    return `${this.serverUrl()}/stac/collections/${encodeURIComponent(collectionId)}/items?${params.toString()}`;
  }

  private async loadPage(nextUrl: string | null, isPagination = false): Promise<void> {
    const dataset = this.dataset;
    if (!dataset) return;

    if (isPagination) {
      this.pageLoading.set(true);
    } else {
      this.initialLoading.set(true);
    }
    this.error.set(null);
    const previousItems = this.items();
    try {
      const datetime = buildStacDatetimeInterval(
      this.appliedFromIso(),
      this.appliedToIso(),
    );
      const page = await this.stacApi.getStacItems(dataset.id, this.serverUrl(), {
        nextUrl,
        limit: this.pageLimit,
        datetime,
      });

      if (isPagination && page.features.length === 0) {
        this.hasNext.set(false);
        this.nextUrl = null;
        return;
      }

      this.items.set(page.features);
      this.nextUrl = page.nextLink;
      this.hasNext.set(Boolean(page.nextLink));

      const fetchUrl = nextUrl || this.buildItemsListUrl(dataset.id);
      if (this.paginationHistory[this.paginationHistory.length - 1] !== fetchUrl) {
        this.paginationHistory = [...this.paginationHistory, fetchUrl];
      }
      this.isFirstPage.set(this.paginationHistory.length <= 1);
    } catch {
      if (isPagination) {
        this.items.set(previousItems);
      }
      this.error.set('Failed to load STAC items. Please try again.');
    } finally {
      this.initialLoading.set(false);
      this.pageLoading.set(false);
    }
  }

  hasThumbnail(item: CanvasStacItem): boolean {
    return Object.values(item.assets ?? {}).some(
      (a) => Array.isArray(a.roles) && a.roles.includes('thumbnail'),
    );
  }

  getThumbnailHref(item: CanvasStacItem): string | null {
    const asset = Object.values(item.assets ?? {}).find(
      (a) => Array.isArray(a.roles) && a.roles.includes('thumbnail'),
    );
    return asset?.href?.trim() || null;
  }

  /** Overview image for plotting (ui-canvas `getOverviewAssetUrl` parity). */
  getOverviewHref(item: CanvasStacItem): string | null {
    const assets = Object.values(item.assets ?? {});
    const withRole = (role: string) =>
      assets.find(
        (a) => Array.isArray(a.roles) && a.roles.includes(role),
      );

    const overview = withRole('overview');
    if (overview?.href?.trim()) return overview.href.trim();

    const data = withRole('data');
    const dataType = data?.type?.toLowerCase() ?? '';
    if (
      data?.href?.trim() &&
      (dataType.startsWith('image/') || dataType.includes('png') || dataType.includes('jpeg'))
    ) {
      return data.href.trim();
    }

    return null;
  }

  onPreview(item: CanvasStacItem): void {
    const href = this.getThumbnailHref(item);
    if (href) {
      this.previewUrl.set(href);
    }
  }

  closePreview(): void {
    this.previewUrl.set(null);
  }

  onPlot(item: CanvasStacItem): void {
    const bbox = resolveStacItemBbox(item);
    if (!bbox) {
      this.error.set(
        'STAC item has no valid bounding box (check geometry or bounds properties).',
      );
      return;
    }
    const overview = this.getOverviewHref(item);
    this.plot.emit({
      imageUrl: overview,
      bbox,
      properties: item.properties ?? {},
    });
  }

  fetchNext(): void {
    if (!this.hasNext() || !this.nextUrl) {
      return;
    }
    void this.loadPage(this.nextUrl, true);
  }

  fetchPrevious(): void {
    if (this.paginationHistory.length > 1) {
      const history = [...this.paginationHistory];
      history.pop();
      const prev = history[history.length - 1];
      this.paginationHistory = history;
      void this.loadPage(prev, true);
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cv-stac-backdrop')) {
      this.close.emit();
    }
  }
}
